import { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { useMeasure } from 'react-use';

// Utils
import { updateActiveItem } from '@/utils/menu/updateActiveItem';
import { getMenuItemsOrder } from '@/utils/menu/getMenuItemsOrder';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';
import { SP_TRANSITION_DURATION_MS } from '@/utils/hooks/socialprofiles/useSocialProfileTransition';

// Types
import type { SocialProfileType } from '@/types/socialProfileType';
import type { SelectedProfilesProps } from '@/components/composite/SelectedProfiles';

// Providers
import { useProfilesStore } from '@/utils/zustand/providers/profiles-provider';

// Components
import { ProfileItem } from '@/components/composite/SelectedProfiles/ProfilesList/ProfileItem';
import { AdditionalProfilesPopover } from '@/components/composite/SelectedProfiles/ProfilesList/AdditionalProfilesPopover';

type ProfilesListProps = Required<SelectedProfilesProps> & {
    profiles: SocialProfileType[];
};

export function ProfilesList({ profiles, itemsSize }: ProfilesListProps) {
    const [ref, { width }] = useMeasure();

    return (
        <div
            ref={ref}
            className={clsx('relative h-9 flex-1', { 'sm:h-11.5': itemsSize === 'base' })}>
            <ProfilesListContent itemsSize={itemsSize} profiles={profiles} width={width} />
        </div>
    );
}

type ProfilesListContentProps = ProfilesListProps & {
    width: number;
};

type OptionsType = {
    itemWidth: number;
    spacing: number;
};

const calculateMaxItems = (totalWidth: number, nbMaxItems: number, options: OptionsType) => {
    const itemWidth = options.itemWidth;
    const spacing = options.spacing;

    const maxItems = Math.floor(totalWidth / (itemWidth + spacing));

    if (maxItems < nbMaxItems) {
        return maxItems - 2;
    }

    return nbMaxItems;
};

function ProfilesListContent({ profiles, width, itemsSize }: ProfilesListContentProps) {
    const isMobile = itemsSize === 'md' || window.innerWidth < 640;
    const launchCalcMaxItems = useCallback(() => {
        return calculateMaxItems(width, profiles.length, {
            itemWidth: isMobile ? 36 : 46,
            spacing: 16
        });
    }, [isMobile, profiles.length, width]);

    const [nbMaxItemDisplayed, setNbMaxItemDisplayed] = useState(launchCalcMaxItems());
    const [initialSocialProfilePosition, setInitialSocialProfilePosition] = useState(0);

    const { updateActiveSocialProfileId } = useProfilesStore((state) => state);
    const { activeSocialProfile } = useActiveSocialProfile();

    const orderedProfiles = useMemo(() => {
        return getMenuItemsOrder({
            storageKey: 'ico-social-profile-list-order',
            menuItems: profiles,
            activeItemId: activeSocialProfile?.id
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(profiles), activeSocialProfile?.id]);

    const updateActive = (profile: SocialProfileType, initialPosition: number) => {
        updateActiveItem<SocialProfileType>({
            storageKey: 'ico-social-profile-list-order',
            itemsList: profiles,
            itemSelectedId: profile.id,
            callback: () => {
                setInitialSocialProfilePosition(initialPosition);
                updateActiveSocialProfileId(profile.id);
                const activeElement = document.activeElement as HTMLButtonElement;
                activeElement.blur();
            }
        });
    };

    useEffect(() => {
        setNbMaxItemDisplayed(launchCalcMaxItems());
    }, [profiles.length, width, launchCalcMaxItems]);

    const displayedProfiles = orderedProfiles.slice(0, nbMaxItemDisplayed);
    const additionalProfiles = orderedProfiles.slice(nbMaxItemDisplayed);

    return (
        <div className="absolute left-0 top-0 flex w-full items-center gap-4">
            {activeSocialProfile ? (
                <motion.div
                    key={activeSocialProfile.id}
                    initial={{
                        translateX: `${initialSocialProfilePosition * 100}%`
                    }}
                    animate={{ translateX: 0 }}
                    transition={{
                        duration: SP_TRANSITION_DURATION_MS / 1000,
                        ease: [0.34, 1.36, 0.64, 1]
                    }}>
                    <ProfileItem
                        size={isMobile ? 'md' : 'base'}
                        profile={activeSocialProfile}
                        isActive
                    />
                </motion.div>
            ) : null}
            {displayedProfiles.map((profile, index) => {
                return (
                    <ProfileItem
                        size={isMobile ? 'md' : 'base'}
                        data-testid="visible-in-menu"
                        key={profile.id}
                        profile={profile}
                        onClick={() => {
                            let initialPosition = 0;
                            if (index > 0 || activeSocialProfile) {
                                initialPosition = index + 1;
                            }
                            updateActive(profile, initialPosition);
                        }}
                    />
                );
            })}
            {additionalProfiles.length ? (
                <AdditionalProfilesPopover
                    profiles={additionalProfiles}
                    itemsSize={itemsSize}
                    updateActive={(profile) => updateActive(profile, nbMaxItemDisplayed + 1)}
                />
            ) : null}
        </div>
    );
}
